import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import {
    useFetch,
    useForm,
    useGrid,
    useStore,
    useToast,
    useResource,
    useModal,
    usePage,
    useFieldArray,
} from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_SURV_RSPN_LIST_SRCH,
    SG_SURV_RSPN_LIST,
    SF_SURV_RSPN_INFO,
} from "./services/PtliSurvRspnMgmtService";
import { SF_SURV_PRTCPNT_QUESTION_LIST } from "@/comn/comn/surv/services/SurvPrtcpntIntService"; // TODO: Service not implemented in dev branch

/*
 * Survey Response Result Detail
 * !== 설문 응답 결과 상세정보 ==!
 */

type SurvItem = { quesItmId: string; itmCn: string; itmOrd?: string | number };

type SurvQuestion = {
    quesId: string;
    quesOrd?: string | number;
    quesCn: string;
    quesTpCd?: string; // "1": 객관식, "2": 주관식 (또는 "OBJ"/"SUB")
    maxSeltCnt?: string | number | null;
    items?: SurvItem[];
};

type SurvPrtcpntPpupProps = {
    qestnrId?: string; // 모달로 띄울 때 주입
    onAfterSubmit?: () => void; // 저장/제출 후 콜백(부모에서 모달 닫고 리스트 리프레시)
    onRequestClose?: () => void; // 모달 닫기 요청(선택)
};

type AnswerRowDto = {
    quesId: string;
    quesItmId?: string | null;
    textAnswer?: string | null;
    quesOrd?: number | null;
    itmOrd?: number | null;
};

export const PTLI0421002Q = (props: SurvPrtcpntPpupProps): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const { quesRspnUsrId } = useParams();
    const routeParams = useParams();

    const [quesList, setQuesList] = useState<SurvQuestion[]>([]);

    const [qestnrId, setQestnrId] = useState<string | null>(null);
    const lastFetchedQidRef = useRef<string | null>(null);

    const [ques, setQues] = useState(false);
    const answersFetchedRef = useRef(false);

    useResource({
        defaultSchema: [],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survRspnInfo: useForm({
            defaultSchema: SF_SURV_RSPN_INFO,
            defaultValues: {},
        }),

        survBody: useForm({
            defaultSchema: SF_SURV_PRTCPNT_QUESTION_LIST, // TODO: Schema not implemented in dev branch
            // defaultSchema: {
            //     id: "form_survBody",
            //     schema: {
            //         questions: {
            //             type: "array",
            //             schema: {
            //                 quesCn: { type: "textarea", label: "L_QUES_CN" },
            //                 quesTpCd: { type: "text", label: "L_QUES_TP_CD" },
            //                 itmCn: { type: "select", label: "L_ITM_CN" },
            //                 itmCns: { type: "checkbox", label: "L_ITM_CNS" },
            //                 itemText: { type: "textarea", label: "L_ITEM_TEXT" },
            //             },
            //         },
            //     },
            // },
            // defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {};

    const ptlTpCd = form.survRspnInfo.watch("ptlTpCd");
    //const qestnrId = form.survRspnInfo.watch("qestnrId");

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */

    const { fields: quesFields, append, remove } = useFieldArray(form.survBody.schema.questions);
    const fetch = {
        getSurvRspnInfo: useFetch({
            api: () => APIS.getSurvRspnInfo(quesRspnUsrId),
            enabled: !!quesRspnUsrId,
            onSuccess: (data) => {
                const survRspnInfo = data.survRspnInfo.content;
                form.survRspnInfo.setValues(survRspnInfo);
                setQestnrId(survRspnInfo?.qestnrId ?? null);
            },
            onError: (error) => {},
            showToast: true,
        }),

        getSurvDtl: useFetch({
            api: () => APIS.getSurvDtl(qestnrId!),
            enabled: false,
            onSuccess: (data) => {
                const content = data?.survDtl?.content ?? data?.survDtl;
                if (!content) return;

                // 질문/항목 정렬
                const toNum = (v: any, def = 999999) => {
                    const n = Number(String(v ?? "").trim());
                    return Number.isFinite(n) ? n : def;
                };
                const sortedQs = (content.questions ?? [])
                    .slice()
                    .sort((a: any, b: any) => toNum(a.quesOrd) - toNum(b.quesOrd))
                    .map((q: any) => ({
                        ...q,
                        items: (q.items ?? []).slice().sort((a: any, b: any) => toNum(a.itmOrd) - toNum(b.itmOrd)),
                    }));

                setQuesList(sortedQs);
                form.survBody.setValue("questions", []); // 내부 상태 초기화
                sortedQs.forEach((q: any) => {
                    append({
                        quesCn: q.quesCn ?? "",
                        quesTpCd: q.quesTpCd ?? "",
                        itmCn: "", // 객관식 선택값
                        itemText: "", // 주관식 입력값
                    });
                });

                queueMicrotask(() => setQues(true));
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00026" }),
            showToast: true,
        }),

        getSurvRspnRslt: useFetch({
            api: () => APIS.getSurvRspnRslt(quesRspnUsrId),
            enabled: false,
            onSuccess: (data) => {
                const answers: AnswerRowDto[] = data?.answers?.content ?? [];

                // quesId 단위로 다중 선택/주관식 묶기
                const answersByQestnrId = answers.reduce((accByQid, currRow) => {
                    const currId = accByQid.get(currRow.quesId) ?? {
                        itmIds: [] as string[],
                        text: null as string | null,
                    };
                    if (currRow.quesItmId) currId.itmIds.push(String(currRow.quesItmId));
                    if (currRow.textAnswer) currId.text = currRow.textAnswer; // 주관식은 마지막 값만 사용(있다면)
                    accByQid.set(currRow.quesId, currId);
                    return accByQid;
                }, new Map<string, { itmIds: string[]; text: string | null }>());

                quesList.forEach((qList, index) => {
                    const quesId = answersByQestnrId.get(qList.quesId);
                    if (!quesId) return;

                    const quesTpCd = String(qList?.quesTpCd ?? "")
                        .trim()
                        .toUpperCase();
                    const max = Math.max(1, Number(qList?.maxSeltCnt ?? 1));

                    if ((quesTpCd === "1" || quesTpCd === "OBJ") && max > 1) {
                        // 객관식, 다중 선택
                        form.survBody.setValue(`questions.${index}.itmCns`, quesId.itmIds ?? []);
                    } else if (quesTpCd === "1" || quesTpCd === "OBJ") {
                        // 객관식, 단일 선택
                        form.survBody.setValue(`questions.${index}.itmCn`, quesId.itmIds?.[0] ?? "");
                    } else if (quesTpCd === "2" || quesTpCd === "SUB" || (qList.items ?? []).length === 0) {
                        // 주관식
                        form.survBody.setValue(`questions.${index}.itemText`, quesId.text ?? "");
                    }
                });
            },
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {};

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getSurvRspnInfo.fetch();
        form.survRspnInfo.setEditable(false);
    }, []);

    useEffect(() => {
        if (!qestnrId) return;
        setQues(false);
        answersFetchedRef.current = false;
        if (lastFetchedQidRef.current === qestnrId) return;
        lastFetchedQidRef.current = qestnrId;
        fetch.getSurvDtl.fetch();
    }, [qestnrId]);

    useEffect(() => {
        if (!ques) return;
        if (answersFetchedRef.current) return;
        answersFetchedRef.current = true;
        fetch.getSurvRspnRslt.fetch();
    }, [ques]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        {
                            /* PTL_TP_CD가 I일 때 출력 */
                            ptlTpCd === "I" && (
                                <>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.usrDmnId}></Group.Control>
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.cstmOfcrNo} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.cstmSctnOrgzCd} />
                                    </Group.Row>

                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.cstmUnitOrgzCd} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.roleNm} />
                                    </Group.Row>
                                </>
                            )
                        }
                        {
                            /* PTL_TP_CD가 I일 때 출력 */
                            (ptlTpCd === "E" || ptlTpCd === "O") && (
                                <>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.usrNm} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.coNm} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.coTin} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.usrTin} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.survRspnInfo.schema.qustnrCoTpCd} />
                                    </Group.Row>
                                </>
                            )
                        }
                        <Group.Row>
                            <Group.Control {...form.survRspnInfo.schema.sysmUseFreqCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Header>
                    <Group.Title title={t("T_QUESTIONS") || "Questions"} />
                </Group.Header>
                <Group.Body>
                    {quesFields.map((fieldRow, index) => {
                        const qList = quesList[index];
                        if (!qList) return null;

                        const quesTpCd = String(qList?.quesTpCd ?? "")
                            .trim()
                            .toUpperCase();
                        const Obj = quesTpCd === "1" || quesTpCd === "OBJ";
                        const Sub = quesTpCd === "2" || quesTpCd === "SUB" || (qList.items ?? []).length === 0;
                        const maxSelt = Math.max(1, Number(qList?.maxSeltCnt ?? 1));

                        fieldRow.schema.quesCn.readOnly = true;

                        const itmList = (qList.items ?? []).map((itm) => ({
                            label: itm.itmCn,
                            value: itm.quesItmId,
                        }));

                        //단일 객관식 행 삽입
                        fieldRow.schema.itmCn.options = itmList;
                        fieldRow.schema.itmCn.hidden = !(Obj && maxSelt <= 1);

                        //다중 객관식 행 삽입
                        fieldRow.schema.itmCns.options = itmList;
                        fieldRow.schema.itmCns.hidden = !(Obj && maxSelt > 1);

                        return (
                            <Group.Section key={fieldRow.key}>
                                <Group.Row>
                                    <Group.Control {...fieldRow.schema.quesCn} readOnly={true} />
                                </Group.Row>

                                {Obj && maxSelt <= 1 && (
                                    <Group.Row>
                                        <Group.Control {...fieldRow.schema.itmCn} readOnly={true} />
                                    </Group.Row>
                                )}

                                {Obj && maxSelt > 1 && (
                                    <Group.Row>
                                        <Group.Control {...fieldRow.schema.itmCns} readOnly={true} />
                                    </Group.Row>
                                )}

                                {Sub && (
                                    <Group.Row>
                                        <Group.Control {...fieldRow.schema.itemText} readOnly={true} />
                                    </Group.Row>
                                )}
                            </Group.Section>
                        );
                    })}
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0421001q);
                        }}
                    ></Button>
                </Layout.Left>
            </Layout>
        </Page>
    );
};
